import React from 'react'
import { Box, Typography, Chip, CircularProgress } from '@mui/material'
import { CardSeeker, TableSeeker, IconSeeker } from '@ui-seeker/core'
import {
    tableData,
    totalCount,
    isLoadingTable,
    autoRefresh,
    nextRefreshCountdown,
    currentPage,
    pageSize
} from '../@signals'
import { useAuditLog } from '../@hooks'
import {
    timestampColumn,
    actionColumn,
    productColumn,
    siteColumn,
    siteCountryCodeColumn,
    siteNameColumn,
    skuColumn,
    sourceColumn,
    userColumn,
    confidenceColumn,
    idAuditColumn,
    idSkuOldColumn,
    idSkuNewColumn,
    userIdColumn,
    propagatedSiteNameColumn,
    propagatedFromSiteColumn,
    iaTextColumn,
    skuOldInfoColumn,
    skuNewInfoColumn,
    productInfoColumn,
    mappingTypeColumn,
    idMatchColumn,
    matchInfoColumn,
    idCustomerColumn
} from './columns'
import ExportButton from '../ExportButton'

export default function AuditLogTable() {
    const { toggleAutoRefresh, fetchAuditData } = useAuditLog()

    const columns = [
        idAuditColumn(),           // 0
        timestampColumn(),         // 1
        actionColumn(),            // 2
        siteColumn(),              // 3 (hidden)
        siteCountryCodeColumn(),   // 4 (hidden)
        siteNameColumn(),          // 5
        productColumn(),           // 6
        productInfoColumn(),       // 7 (hidden)
        idSkuOldColumn(),          // 8 (hidden)
        idSkuNewColumn(),          // 9 (hidden)
        skuOldInfoColumn(),        // 10 (hidden)
        skuNewInfoColumn(),        // 11 (hidden)
        skuColumn(),               // 12
        sourceColumn(),            // 13
        userIdColumn(),            // 14 (hidden)
        userColumn(),              // 15
        confidenceColumn(),        // 16
        propagatedSiteNameColumn(), // 17 (hidden)
        propagatedFromSiteColumn(), // 18 (hidden - used by actionColumn for icon)
        iaTextColumn(),            // 19 (hidden)
        mappingTypeColumn(),       // 20 (hidden - used by skuColumn for raw2raw)
        idMatchColumn(),           // 21 (hidden - used by skuColumn for raw2raw)
        matchInfoColumn(),         // 22 (hidden - match origin_name + image)
        idCustomerColumn()         // 23 (hidden - needed by raw2raw editor for map/unmap)
    ]

    const handlePageChange = (newPage) => {
        currentPage.value = newPage
        fetchAuditData()
    }

    const handleRowsPerPageChange = (newRowsPerPage) => {
        pageSize.value = newRowsPerPage
        currentPage.value = 0
        fetchAuditData()
    }

    return (
        <CardSeeker>
            <Box sx={{ p: 2 }}>
                {/* Header with streaming controls */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6">
                            Audit Records
                        </Typography>
                        <Chip
                            label={`${totalCount.value.toLocaleString()} total`}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Auto-refresh indicator */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                cursor: 'pointer',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                bgcolor: autoRefresh.value ? 'success.lighter' : 'grey.100',
                                '&:hover': { bgcolor: autoRefresh.value ? 'success.light' : 'grey.200' }
                            }}
                            onClick={toggleAutoRefresh}
                        >
                            {autoRefresh.value ? (
                                <>
                                    <IconSeeker icon="circle-play" color="success" />
                                    <Typography variant="body2" color="success.dark" fontWeight="medium">
                                        Live ({nextRefreshCountdown.value}s)
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <IconSeeker icon="circle-pause" color="text.secondary" />
                                    <Typography variant="body2" color="text.secondary">
                                        Paused
                                    </Typography>
                                </>
                            )}
                        </Box>
                        <ExportButton />
                    </Box>
                </Box>

                {/* Loading indicator */}
                {isLoadingTable.value && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Table */}
                {!isLoadingTable.value && (
                    <TableSeeker
                        data={tableData.value}
                        columns={columns}
                        options={{
                            selectableRows: 'none',
                            filterType: 'dropdown',
                            responsive: 'standard',
                            rowsPerPage: pageSize.value,
                            rowsPerPageOptions: [25, 50, 100, 200],
                            count: totalCount.value,
                            page: currentPage.value,
                            serverSide: true,
                            onChangePage: handlePageChange,
                            onChangeRowsPerPage: handleRowsPerPageChange,
                            sortOrder: {
                                name: 'created_at',
                                direction: 'desc'
                            },
                            textLabels: {
                                body: {
                                    noMatch: 'No audit records found'
                                }
                            },
                            setRowProps: (row, dataIndex) => {
                                const record = tableData.value[dataIndex]
                                if (record?.propagated_from_site) {
                                    return {
                                        style: { backgroundColor: 'rgba(33, 150, 243, 0.04)' }
                                    }
                                }
                                if (record?.mapping_type === 'raw2raw') {
                                    return {
                                        style: { backgroundColor: 'rgba(124, 58, 237, 0.03)' }
                                    }
                                }
                                return {}
                            }
                        }}
                    />
                )}
            </Box>
        </CardSeeker>
    )
}
